/**
 * Enterprise app shell — provider tree + boot sequence + React Router pages.
 * Ported from flowstate-automate App.tsx for TanStack Start __root.tsx.
 */
import { useState, useEffect, useCallback, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useRouterState } from "@tanstack/react-router";
import { CopyFeedbackAria } from "@/components/accessibility/CopyFeedbackAria";
import { ErrorBoundary } from "@/components/utility/ErrorBoundary";
import { ToastNotificationProvider } from "@/components/notifications/ToastNotificationProvider";
import { Toaster, ToastDurationProvider } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/hooks/useAuth";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { CoreServiceProvider } from "@/contexts/CoreServiceProvider";
import { AuthenticatedServiceProvider } from "@/contexts/AuthenticatedServiceProvider";
import { ChatProvider } from "@/contexts/ChatContext";
import { CortexChatProvider } from "@/contexts/CortexChatContext";
import { GamificationThemeProvider } from "@/contexts/GamificationThemeContext";
import { SeasonsProvider } from "@/contexts/SeasonsContext";
import { RealtimeTasksProvider } from "@/contexts/RealtimeTasksContext";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { PWAUpdateNotifier } from "@/components/pwa/PWAUpdateNotifier";
import SiteBackground from "@/components/layout/SiteBackground";
import { ThemePreferencesSync } from "@/components/theme/ThemePreferencesSync";
import SystemBootSequence from "@/components/system/SystemBootSequence";
import { WalkthroughProvider, WalkthroughRenderer } from "@/features/walkthrough";
import { DemoEngineProvider } from "@/features/demo-engine";
import { VerificationStudioBanner, VerificationStudioProvider } from "@/features/verification-studio";
import { FeatureShowcaseProvider, FeatureShowcase } from "@flowstate/onboarding";
import "@flowstate/onboarding/styles.css";
import { createSupabasePersistence } from "@/services/onboarding/supabase-persistence";
import { queryClient } from "@/lib/queryClient";
import { useMyceliumQueryBridge } from "@/hooks/useMyceliumQueryBridge";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSubscriptionNotifications } from "@/hooks/accounting/useSubscriptionNotifications";
import { useOrchestrationEvents } from "@/hooks/useOrchestrationEvents";
import { ConsoleBottomPanel as ConsoleDrawer, redactSecretsDeep } from "@flowstate/console";
import { useChatContext } from "@/contexts/ChatContext";
import type { CortexEventLogRow } from "@flowstate/types";
import { ClientBoot } from "@/components/ClientBoot";
import { logger } from "@/lib/logger";
import AiAgent from "@/features/ai-agent";
import { AICredentialsOnboarding } from "@/components/onboarding/AICredentialsOnboarding";

const showcasePersistence = createSupabasePersistence();

const DocumentTitleUpdater = () => {
  useDocumentTitle();
  return null;
};

const MyceliumQueryBridge = () => {
  useMyceliumQueryBridge();
  return null;
};

const OrchestrationConsoleMount = () => {
  const { user } = useAuth();
  const { isChatOpen } = useChatContext();
  // Only render on admin routes (inside the _admin layout) — not on home/landing/auth.
  const isAdminRoute = useRouterState({
    select: (s) => s.matches.some((m) => m.routeId.startsWith("/_admin")),
  });
  const { events, isLoading, isConnected, clear } = useOrchestrationEvents({
    enabled: !!user,
    limit: 100,
  });

  const handleExport = useCallback((evts: CortexEventLogRow[]) => {
    const ndjson = evts
      .map((e) => JSON.stringify(redactSecretsDeep(e)))
      .join("\n");
    const blob = new Blob([ndjson], { type: "application/x-ndjson" });
    const url = URL.createObjectURL(blob);
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orchestration-export-${ts}.ndjson`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  if (!user || !isAdminRoute) return null;

  return (
    <ConsoleDrawer
      events={events}
      isLoading={isLoading}
      isConnected={isConnected}
      onClear={clear}
      onExport={handleExport}
      hideFloatingTrigger={isChatOpen}
    />
  );
};

const AppShellChrome = () => (
  <>
    <PWAUpdateNotifier />
    <Toaster />
    <CopyFeedbackAria />
    <OrchestrationConsoleMount />
  </>
);

const AppContent = ({ children }: { children?: ReactNode }) => {
  const { user } = useAuth();

  useSubscriptionNotifications({
    daysAhead: 7,
    checkIntervalMinutes: 60,
    enabled: !!user,
  });

  // TanStack Router owns every URL — the legacy React Router shell is retired.
  return (
    <ToastNotificationProvider>
      <ToastDurationProvider>
        <DocumentTitleUpdater />
        <SiteBackground />
        <AppShellChrome />
        {children}
      </ToastDurationProvider>
    </ToastNotificationProvider>
  );
};

const AppWithBoot = ({ children }: { children?: ReactNode }) => {
  const { user } = useAuth();
  const [bootProgress, setBootProgress] = useState(0);
  const [isBooting, setIsBooting] = useState(false);
  const [bootComplete, setBootComplete] = useState(false);

  useEffect(() => {
    if (user && !bootComplete) {
      setIsBooting(true);
      setBootProgress(0);
    } else if (!user) {
      setBootComplete(false);
      setIsBooting(false);
      setBootProgress(0);
    }
  }, [user, bootComplete]);

  const handleBootProgress = useCallback((_step: string, progress: number) => {
    setBootProgress(progress);
    if (progress >= 100) {
      setTimeout(() => {
        setBootComplete(true);
        setIsBooting(false);
      }, 800);
    }
  }, []);

  if (!user) {
    return <AppContent>{children}</AppContent>;
  }

  return (
    <>
      {isBooting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <SystemBootSequence
            progress={bootProgress}
            isComplete={bootComplete}
            onComplete={() => setBootComplete(true)}
          />
        </div>
      )}

      <AuthenticatedServiceProvider onInitProgress={handleBootProgress}>
        <div className={isBooting ? "invisible" : "visible"}>
          <AppContent>{children}</AppContent>
        </div>
      </AuthenticatedServiceProvider>
    </>
  );
};

export function AppShell({ children }: { children?: ReactNode }) {
  return (
    <>
      <ClientBoot />
      <ThemePreferencesSync />
      <WalkthroughRenderer />
      <FeatureShowcase />
      <AICredentialsOnboarding />
      <MyceliumQueryBridge />
      <AppWithBoot>{children}</AppWithBoot>
      <AiAgent />
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
      )}
    </>
  );
}

export function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <CoreServiceProvider>
            <AuthProvider>
              <RealtimeTasksProvider>
                <GamificationThemeProvider>
                  <SeasonsProvider>
                    <ChatProvider>
                      <CortexChatProvider>
                      <NotificationProvider>
                        <WalkthroughProvider>
                          <VerificationStudioProvider>
                            <DemoEngineProvider>
                              <FeatureShowcaseProvider persistence={showcasePersistence}>
                                {children}
                                <VerificationStudioBanner />
                              </FeatureShowcaseProvider>
                            </DemoEngineProvider>
                          </VerificationStudioProvider>
                        </WalkthroughProvider>
                      </NotificationProvider>
                      </CortexChatProvider>
                    </ChatProvider>
                  </SeasonsProvider>
                </GamificationThemeProvider>
              </RealtimeTasksProvider>
            </AuthProvider>
          </CoreServiceProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
